import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./LoginModal.css";
import { handleAppError } from "utils/errorHandler";
import ButtonLoader from "./ButtonLoader";

const loginSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .trim()
    .required("Password is required"),
});

function LoginModal({
  show,
  handleClose,
  onSignIn = () => {},
  onSignUp = () => {},
  onForgotPassword = () => {},
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState(null);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      // rememberMe: false,
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setApiError(null);
        console.log("Login attempt with:", values);

        // Call the onSignIn prop with form values
        // Parent will handle closing modal and showing toast
        await onSignIn(values.email, values.password);
        resetForm();
      } catch (error) {
        const errorMsg = handleAppError(error);
        setApiError(errorMsg);
      }
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      formik.resetForm();
      setApiError(null);
    }
  }, [show]);

  return (
    <Modal show={show} onHide={handleClose} centered size="open">
      <div className="p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h1 className="h4 mb-0">
            Welcome Back <span>👋</span>
          </h1>
          <button
            className="btn-close"
            onClick={handleClose}
            aria-label="Close modal"
          />
        </div>

        <p className="text-muted mb-4 login-info">
          Please enter your details to sign in.
        </p>

        {/* API Error Display */}
        {apiError && (
          <div className="alert alert-danger" role="alert">
            {apiError}
          </div>
        )}

        {/* Form */}
        <Form onSubmit={formik.handleSubmit}>
          {/* Email Field */}
          <div className="mb-3">
            <Form.Label htmlFor="email" className="fw-medium">
              Email
            </Form.Label>
            <Form.Control
              type="email"
              id="email"
              placeholder="Enter your email"
              className={`bg-light ${
                formik.touched.email && formik.errors.email ? "is-invalid" : ""
              }`}
              {...formik.getFieldProps("email")}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="invalid-feedback d-block">
                {formik.errors.email}
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="mb-3">
            <Form.Label htmlFor="password" className="fw-medium">
              Password
            </Form.Label>
            <div className="password-input-container position-relative">
              <Form.Control
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="Enter password"
                className={`bg-light password-input ${
                  formik.touched.password && formik.errors.password
                    ? "is-invalid"
                    : ""
                }`}
                {...formik.getFieldProps("password")}
              />
              <button
                type="button"
                className="password-toggle-btn position-absolute"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  {showPassword ? (
                    <path
                      d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : (
                    <>
                      <path
                        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <div className="invalid-feedback d-block">
                {formik.errors.password}
              </div>
            )}
          </div>

          {/* Options Row */}
          <div className="d-flex justify-content-end align-items-center mb-4">
            {/* <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="rememberMe"
                {...formik.getFieldProps("rememberMe")}
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Remember me
              </label>
            </div> */}
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              style={{ color: "#ff69b4" }}
              onClick={onForgotPassword}
            >
              Forgot Password?
            </button>
          </div>

          {/* Privacy Policy and Terms of Service */}
          <div className="mb-4">
            <span>
              By signing in, you agree to our{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="privacy-consent-link"
              >
                Privacy Policy
              </a>
              {" and our "}
              <a
                href="/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="privacy-consent-link"
              >
                Terms of Service
              </a>
            </span>
          </div>

          {/* Sign In Button */}
          <Button
            type="submit"
            disabled={formik.isSubmitting}
            className="w-100 rounded-pill py-2 mb-4"
            style={{
              background: "#FFC3F6",
              border: "none",
              color: "#000",
            }}
          >
            {formik.isSubmitting ? <ButtonLoader /> : "Sign In"}
          </Button>
        </Form>

        {/* Sign Up Link */}
        <div className="d-flex justify-content-center gap-2">
          <span className="text-muted">Don't have an account?</span>
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            style={{ color: "#ff69b4" }}
            onClick={onSignUp}
          >
            Create Account
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default LoginModal;
